import assert from 'owa-assert/lib/assert';
import { CoprincipalMailboxRank, getAccountKeyForMailboxInfo } from 'owa-client-types';
import { errorThatWillCauseAlert, trace } from 'owa-trace';
import type { TraceErrorObject } from 'owa-analytics-types';
import addCoprincipalAccountMutator from '../actions/addCoprincipalAccount';
import onCoprincipalAccountAdded from '../actions/onCoprincipalAccountAdded';
import { updateCoprincipalUserIdentities } from './coprincipalUserIdentities';
import { interceptAndAlertOnMailboxInfoChanges } from './interceptAndAlertOnMailboxInfoChanges';
import { updateMailboxInfo } from './updateMailboxInfo';
import type { CoprincipalAccountSource } from '../store/schema/AccountSourceList';
import { updateCoprincipalAccountContracts } from './updateCoprincipalAccountContracts';
import { logUsageFromAccounts } from 'owa-account-analytics';
import { scrubForPii, ScrubType } from 'owa-config/lib/scrubForPii';
import getCoprincipalAccountForMailboxInfo from './getCoprincipalAccountForMailboxInfo';

/**
 * Gets the diagnostic data for the duplicate accountKey error
 * @param existingAccount Existing account that has the accountKey value
 * @param newAccount New account that has the same accountKey value
 * @returns Diagnostic data for the duplicate accountKey error
 */
function getDuplicateAccountKeyDiagnosticData(
    existingAccount: CoprincipalAccountSource,
    newAccount: CoprincipalAccountSource
) {
    return {
        type: newAccount.sourceType,
        existingType: existingAccount.sourceType,
        areSameType: newAccount.sourceType === existingAccount.sourceType,
        areSameSmtp:
            newAccount.mailboxInfo.mailboxSmtpAddress ===
            existingAccount.mailboxInfo.mailboxSmtpAddress,
        areSameUid:
            newAccount.mailboxInfo.userIdentity === existingAccount.mailboxInfo.userIdentity,
        areSamePersistenceId: newAccount.persistenceId === existingAccount.persistenceId,
    };
}

/**
 * Checks to make sure that the accountKey value of the coprincipal account is unique. If the accountKey
 * value is not unique this method will throw
 * @param accountSource Coprincipal account that is to be added
 */
function checkThatAccountKeyIsUnique(accountSource: CoprincipalAccountSource) {
    const accountKey = getAccountKeyForMailboxInfo(accountSource.mailboxInfo);

    if (accountKey !== accountSource.sourceId) {
        // The accountKey must always be the sourceId of the coprincipal account.
        errorThatWillCauseAlert('AddAccount-AccountKeyMismatch');
    }

    const existingAccount = getCoprincipalAccountForMailboxInfo(accountSource.mailboxInfo);
    if (!!existingAccount) {
        // There is already a coprincipal account with the same accountKey in the store. This account
        // should not have be added to the store and we should alert the system that there is an
        // error in the build.
        const error: TraceErrorObject = new Error('DuplicateCoprincipalAccountKey');
        error.additionalInfo = getDuplicateAccountKeyDiagnosticData(existingAccount, accountSource);
        errorThatWillCauseAlert('DuplicateCoprincipalAccountKey', error);
    }
}

/**
 * Preforms validation on the account source and then adds the account
 * @param accountSource
 */
export default function addCoprincipalAccount(accountSource: CoprincipalAccountSource): void {
    const existingAccount = getCoprincipalAccountForMailboxInfo(accountSource.mailboxInfo);
    if (existingAccount != undefined) {
        assert(false, 'Found duplicated account in the store.', () =>
            JSON.stringify(getDuplicateAccountKeyDiagnosticData(existingAccount, accountSource))
        );
    }

    assert(accountSource.mailboxRank === CoprincipalMailboxRank, 'Must be a Coprincipal account');

    trace.info(
        `[source-list-store] Adding coprincipal account with boot state ${accountSource.bootState}`
    );
    /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param, owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
     * BASELINE: DO NOT COPY/PASTE!
     * undefined
     *	> Argument at index 1 for logUsageFromAccounts must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo
     (https://aka.ms/OWALintWiki)
         * Baseline. Dev touching this file must update to pass the active MailboxInfo; Docs: https://aka.ms/owa-analytics-mailboxinfo
         * undefined
         *	> MailboxInfo must be passed into the function to identify the account.
         (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
             *	> Invalid Outlook telemetry EventName "addCoprincipalAccount": must start with a capital letter (A–Z). */
    logUsageFromAccounts('addCoprincipalAccount', undefined, {
        sourceId: scrubForPii(accountSource.sourceId, true, ScrubType.Email),
        persistenceId: scrubForPii(accountSource.persistenceId, true, ScrubType.Email),
        sourceType: accountSource.sourceType,
        callstack: new Error('addCoprincipalAccount').stack,
    });

    accountSource = updateMailboxInfo(accountSource) as CoprincipalAccountSource;
    accountSource = updateCoprincipalAccountContracts(accountSource);
    checkThatAccountKeyIsUnique(accountSource);
    addCoprincipalAccountMutator(accountSource);

    interceptAndAlertOnMailboxInfoChanges(accountSource.sourceId);
    updateCoprincipalUserIdentities();
    onCoprincipalAccountAdded(accountSource);
}
